import Button from "@lhd/components/Button";
import { Tabs } from "antd";
import produce from "immer";
import _, { omit } from "lodash";
import React, { FC, useCallback, useEffect } from "react";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { BiSave } from "react-icons/bi";
import { useImmer } from "use-immer";
import { generateUuid } from "../utils";
import AppFormLayout from "./AppFormLayout";
import AppFormInformation from "./components/AppFormInformation";
import TabPermissionCreate from "./components/TabPermissionCreate";
import {
  APP_PART_TYPES,
  COLUMN_WIDTH,
  ITEM_SIZE_RELY_ON_CONTENT_TYPES,
  TAGS,
} from "./constants";
import useFormAction, { ReceivedProps, useAppFormCreate } from "./hook";
import { StylesAppFormCreate } from "./styled";
import type {
  AppPartType,
  FormActionContextState,
  FormActionContextType,
} from "./types";
import { useSubmitFormStateFormik } from "@lhd/components/FormState/hook";

export const FormActionContext = React.createContext<FormActionContextType>({
  contextState: {
    layout: [],
    layoutDeleted: undefined,
    isHoverParentItem: false,
    resetLayout: true,
    isDisableDragging: false,
    isError: false,
    hasOpenedContinueUsingBizSheetModal: false,
  },
  setContextState: () => {},
  onHandleEditItem: () => {},
  onHandleDeleteItem: () => {},
  onHandleDuplicate: () => {},
  onLayoutChange: () => {},
  onHandleDropChange: () => {},
  onHandleAddItemToParentItem: () => {},
  onHandleReorderItemToParentItem: () => {},
  onHandleChangeFieldCode: (currentI: string, newFieldCode: string) => {
    return `${currentI}${newFieldCode}`;
  },
});

export const AppFormContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [contextState, setContextState] = useImmer<FormActionContextState>({
    layout: [],
    layoutDeleted: [],
    isHoverParentItem: false,
    resetLayout: true,
    isDisableDragging: false,
    isError: false,
    hasOpenedContinueUsingBizSheetModal: false,
  });

  const isError =
    contextState.layout &&
    contextState.layout.some((item) => {
      if (item.app_part_type === APP_PART_TYPES.LABEL) return false;
      return !item.field_code || !item.name;
    });

  useEffect(() => {
    setContextState((draft) => {
      draft.isError = isError;
    });
  }, [isError]);

  const onLayoutChange = (
    layout: ReactGridLayout.Layout[],
    parent?: AppPartType,
  ) => {
    setTimeout(() => {
      setContextState((draft) => {
        if (parent) {
          const parentIndex = draft.layout.findIndex(
            (a) => a.item_config_data.i === parent.item_config_data.i,
          );
          if (parentIndex === -1) return;
          const appPart = draft.layout[parentIndex];
          if (parent?.item_config_data && parentIndex > -1)
            draft.layout[parentIndex].item_config_data.w =
              parent.item_config_data.w;
          layout.forEach((item: any) => {
            if (appPart.children) {
              const currentItemIndex = appPart.children.findIndex(
                (a) => a.item_config_data.i === item.i,
              );

              if (currentItemIndex > -1) {
                const childAppPart = appPart.children[currentItemIndex];
                appPart.children[currentItemIndex] = {
                  ...childAppPart,
                  item_config_data: {
                    ...childAppPart.item_config_data,
                    ...item,
                  },
                };
              }
            }
          });
        } else {
          layout.forEach((item: any) => {
            const currentItemIndex = draft.layout.findIndex(
              (a) => a.item_config_data.i === item.i,
            );

            if (currentItemIndex > -1) {
              const appPart = draft.layout[currentItemIndex];
              draft.layout[currentItemIndex] = {
                ...appPart,
                item_config_data: { ...appPart.item_config_data, ...item },
              };
            }
          });
        }
      });
    }, 0);
  };
  const onHandleChangeFieldCode = useCallback(
    (currentI: string, newFieldCode: string) => {
      const duplicateItem = contextState.layout.find(
        (item) =>
          item.field_code === newFieldCode &&
          item.item_config_data.i !== currentI,
      );
      if (duplicateItem) {
        const fieldCode = duplicateItem.field_code?.split("_")[0];
        const id = duplicateItem.field_code?.split("_")[1];
        return `${fieldCode}_${Number(id) + 1}`;
      }
      return newFieldCode;
    },
    [contextState.layout],
  );

  const onHandleDeleteItem = useCallback(
    ({ i, parentI }: { i: string; parentI?: string }) => {
      setContextState((draft) => {
        const parseConfig = (config: any) => {
          if (!config) return {};
          if (typeof config === "string") {
            try {
              return JSON.parse(config);
            } catch {
              return {};
            }
          }
          return config;
        };

        const updateEmployeeInfoParentChildren = (
          employeeInfoParentI?: string,
          deletedAppPartType?: string,
        ) => {
          if (!employeeInfoParentI || !deletedAppPartType) return;
          const updateParent = (parent: AppPartType) => {
            const config = parseConfig(parent.item_config_data);
            if (
              config?.employee_info_children &&
              Array.isArray(config.employee_info_children)
            ) {
              config.employee_info_children =
                config.employee_info_children.filter(
                  (type: string) => type !== deletedAppPartType,
                );
              parent.item_config_data = config;
            }
          };

          const parentInLayout = draft.layout.find((item) => {
            const config = parseConfig(item.item_config_data);
            return config?.i === employeeInfoParentI;
          });
          if (parentInLayout) {
            updateParent(parentInLayout);
            return;
          }

          for (const item of draft.layout) {
            if (item.children) {
              const childParent = item.children.find((c) => {
                const config = parseConfig(c.item_config_data);
                return config?.i === employeeInfoParentI;
              });
              if (childParent) {
                updateParent(childParent);
                return;
              }
            }
          }
        };

        if (parentI) {
          const parentIndex = draft.layout.findIndex(
            (item) => parseConfig(item.item_config_data).i === parentI,
          );
          if (parentIndex > -1 && draft.layout[parentIndex].children) {
            const deletedChild = draft.layout[parentIndex].children?.find(
              (c) => parseConfig(c.item_config_data).i === i,
            );
            const remainingChildren = draft.layout[
              parentIndex
            ].children?.filter((c) => parseConfig(c.item_config_data).i !== i);
            draft.layout[parentIndex].children = remainingChildren;

            if (deletedChild) {
              const deletedChildConfig = parseConfig(
                deletedChild.item_config_data,
              );
              if (!draft.layoutDeleted) {
                draft.layoutDeleted = [];
              }

              if (!draft.layoutDeleted[parentIndex]) {
                draft.layoutDeleted[parentIndex] = {
                  ...draft.layout[parentIndex],
                  children: [],
                };
              }

              const parentDeletedItem = draft.layoutDeleted[parentIndex];
              if (parentDeletedItem) {
                if (!parentDeletedItem.children) {
                  parentDeletedItem.children = [];
                }
                parentDeletedItem.children.push(deletedChild);
              }

              updateEmployeeInfoParentChildren(
                deletedChildConfig?.employee_info_parent_i,
                deletedChild.app_part_type,
              );

              if (
                deletedChild.app_part_type ===
                APP_PART_TYPES.EMPLOYEE_INFO_SELECTION
              ) {
                draft.layout[parentIndex].children = draft.layout[
                  parentIndex
                ].children?.filter(
                  (c) =>
                    parseConfig(c.item_config_data)?.employee_info_parent_i !==
                    i,
                );
              }
            }
          }
        } else {
          const deletedItem = draft.layout.find(
            (item) => parseConfig(item.item_config_data).i === i,
          );

          draft.layout = draft.layout.filter(
            (item) => parseConfig(item.item_config_data).i !== i,
          );

          if (deletedItem) {
            const deletedConfig = parseConfig(deletedItem.item_config_data);
            if (!draft.layoutDeleted) {
              draft.layoutDeleted = [];
            }
            draft.layoutDeleted.push(deletedItem);

            updateEmployeeInfoParentChildren(
              deletedConfig?.employee_info_parent_i,
              deletedItem.app_part_type,
            );

            if (
              deletedItem.app_part_type ===
              APP_PART_TYPES.EMPLOYEE_INFO_SELECTION
            ) {
              draft.layout = draft.layout.filter(
                (item) =>
                  parseConfig(item.item_config_data)?.employee_info_parent_i !==
                  i,
              );
              draft.layout.forEach((item) => {
                if (item.children) {
                  item.children = item.children.filter(
                    (c) =>
                      parseConfig(c.item_config_data)
                        ?.employee_info_parent_i !== i,
                  );
                }
              });
            }
          }
        }
      });
    },
    [setContextState],
  );

  const onHandleEditItem = ({
    editItem,
    preItemConfig,
  }: {
    editItem: AppPartType;
    preItemConfig?: AppPartType["item_config_data"];
  }) => {
    setContextState((draft) => {
      const parentId = editItem?.item_config_data.parentI;
      const childrenIndex = editItem?.item_config_data.childIndex;
      const i = parentId ? parentId : editItem.item_config_data.i;
      const foundItemIndex = draft.layout.findIndex(
        (item) => item.item_config_data.i === i,
      );
      if (foundItemIndex > -1) {
        const parentItem = draft.layout[foundItemIndex];
        if (
          ITEM_SIZE_RELY_ON_CONTENT_TYPES.includes(editItem.app_part_type) &&
          preItemConfig
        ) {
          const newConfig = editItem.item_config_data;
          if (
            !editItem.item_config_data.i &&
            (preItemConfig.w !== newConfig.w || preItemConfig.h !== newConfig.h)
          ) {
            editItem.item_config_data.i = generateUuid();
          }
        }
        if (
          parentId &&
          typeof childrenIndex === "number" &&
          childrenIndex > -1 &&
          parentItem.children
        ) {
          parentItem.children[childrenIndex] = editItem;
        } else {
          draft.layout[foundItemIndex] = editItem;
        }
      }
    });
  };

  const onHandleDropChange = (
    newData: AppPartType,
    parentAppPart?: AppPartType,
  ) => {
    setContextState((draft) => {
      if (parentAppPart) {
        onHandleAddItemToParentItem(newData, parentAppPart.item_config_data.i);
      } else {
        draft.layout.push(newData);
      }
    });
  };

  const generateNewChildId = useCallback(
    (parentId: string) => {
      const layout = contextState?.layout;
      if (layout) {
        const children = layout.find(
          (l) => l.item_config_data.i === parentId,
        )?.children;
        if (children) {
          const ids = children.map((l) => {
            const id = l.item_config_data.i.split("-");
            return Number(id[id.length - 1]);
          });
          const nextId = String((ids.length > 0 ? Math.max(...ids) : 0) + 1);
          return `${parentId}-${nextId}`;
        }
      }
      return `${parentId}-1`;
    },
    [contextState.layout],
  );

  const onHandleAddItemToParentItem = useCallback(
    (newItem: AppPartType, parentI: string) => {
      setContextState((draft) => {
        const parentIndex = draft.layout.findIndex(
          (item) => item.item_config_data.i === parentI,
        );
        if (parentIndex > -1) {
          const line_index =
            draft.layout?.find((l) => l.item_config_data.i === parentI)
              ?.children?.length || 0;
          const newItemModified = produce(newItem, (draft) => {
            draft.item_config_data.parentI = parentI;
            draft.item_config_data.i = generateNewChildId(parentI);
            draft.line_index = line_index;
            draft.item_config_data.childIndex = line_index;
            draft.id = undefined;
          });
          draft.layout
            ?.find((l) => l.item_config_data.i === parentI)
            ?.children?.push(newItemModified);
        }
      });
    },
    [setContextState, generateNewChildId],
  );

  const onHandleDuplicate = useCallback(
    ({
      originalItem,
      parentI,
      containerRef,
    }: {
      originalItem: AppPartType;
      parentI?: string;
      containerRef?: React.RefObject<HTMLDivElement>;
    }) => {
      setContextState((draft) => {
        if (parentI) {
          onHandleAddItemToParentItem(originalItem, parentI);
        } else {
          const {
            app_part_type,
            item_config_data: { x: originX, w: itemW, h: itemH },
          } = originalItem;
          const clonedAppPart = _.cloneDeep(originalItem);
          const newId = generateUuid();
          const {
            x: xCloned,
            y: yCloned,
            w: wCloned,
            h: hCloned,
          } = clonedAppPart.item_config_data;
          let remainSpaceX: number | null = null;
          let newX: number = xCloned + wCloned;

          const bottomCloned = yCloned + hCloned;

          draft.layout.forEach((item) => {
            const { y, h, x, w } = item.item_config_data;
            const bottom = y + h;
            if (
              (bottom <= bottomCloned && bottom >= yCloned) ||
              (y >= yCloned && y < bottomCloned) ||
              (y < yCloned && bottom > bottomCloned && x > xCloned)
            ) {
              if (remainSpaceX === null) {
                remainSpaceX = item.item_config_data.x - xCloned;
              }
              remainSpaceX = Math.min(
                remainSpaceX,
                item.item_config_data.x - xCloned,
              );
              newX = Math.max(newX, x + w);
              return true;
            }
            return false;
          });
          let isAttention = false;

          if (containerRef?.current?.parentElement) {
            const scrollLeft = containerRef.current.parentElement.scrollLeft;
            const offsetWidth = containerRef.current.parentElement.offsetWidth;
            const newScrollLeft =
              newX * COLUMN_WIDTH + 30 + wCloned * COLUMN_WIDTH - offsetWidth;
            if (newScrollLeft > scrollLeft) {
              isAttention = true;
              containerRef.current.parentElement.scrollLeft = newScrollLeft;
            }
          }
          const duplicateItem = {
            ...clonedAppPart,
            id: undefined,
            is_attention: isAttention,
            item_config_data: {
              ...clonedAppPart.item_config_data,
              parentI: undefined,
              i: newId,
              x: newX,
            },
          };

          // const fieldCode = `${app_part_type}_${draft.layout.length - 1}`;
          const order =
            contextState.layout.filter(
              (item) => item.app_part_type === app_part_type,
            ).length - 1;
          const fieldCode = `${
            TAGS.find((t) => t.app_part_type === app_part_type)?.name
          }_${order}`;
          duplicateItem.field_code = onHandleChangeFieldCode(
            duplicateItem.item_config_data.i,
            fieldCode,
          );
          if (
            APP_PART_TYPES.TABLE === duplicateItem.app_part_type &&
            duplicateItem?.children &&
            duplicateItem?.children?.length > 0
          ) {
            for (let i = 0; i < (duplicateItem?.children?.length ?? 0); i++) {
              duplicateItem.children[i] = omit(duplicateItem.children[i], [
                "id",
                "parent_id",
                "app_id",
                "sort_index",
                "updated_at",
                "created_at",
              ]) as AppPartType;
              if (duplicateItem.children[i].item_config_data) {
                duplicateItem.children[i].item_config_data = {
                  ...duplicateItem.children[i].item_config_data,
                  parentI: newId,
                  i: `${newId}-${i + 1}`,
                };
              }
            }
          }
          draft.layout = [duplicateItem, ...draft.layout];
        }
      });
    },
    [
      setContextState,
      onHandleAddItemToParentItem,
      contextState.layout,
      onHandleChangeFieldCode,
    ],
  );
  const onHandleReorderItemToParentItem = (
    startIndex: number,
    endIndex: number,
    parentI: string,
  ) => {
    setContextState((draft) => {
      const parentIndex = draft.layout.findIndex(
        (item) => item.item_config_data.i === parentI,
      );
      if (parentIndex > -1) {
        const [removed] =
          draft.layout[parentIndex].children?.splice(startIndex, 1) ?? [];
        draft.layout[parentIndex].children?.splice(endIndex, 0, removed);
      }
      return draft;
    });
  };
  return (
    <FormActionContext.Provider
      value={{
        contextState,
        setContextState,
        onHandleEditItem,
        onHandleDeleteItem,
        onHandleDuplicate,
        onLayoutChange,
        onHandleChangeFieldCode,
        onHandleDropChange,
        onHandleAddItemToParentItem,
        onHandleReorderItemToParentItem,
      }}
    >
      {children}
    </FormActionContext.Provider>
  );
};

const AppFormCreateLayout: FC<ReceivedProps> = () => {
  const {
    onFinish,
    onChange,
    DEFAULT_TAB,
    activeKey,
    formik,
    isSubmitting,
    displaySize,
    height,
    size,
  } = useAppFormCreate();

  const listKey = [`app-table`, `app-grid`, `app-information`, `permission`];

  const { getButtonStyle, markSubmit, registerFormWatcher, getFieldClassName } =
    useSubmitFormStateFormik(formik, false, ["name", "categories"]);

  return (
    <>
      {/* <div className="header">
        <h2>Bizシート作成</h2>
      </div> */}

      <StylesAppFormCreate text_scale={displaySize}>
        {listKey.slice(0, 3).includes(activeKey) && (
          <div className="wrap-button-save">
            <Button
              loading={isSubmitting}
              icon={
                <div className="flex-center">
                  <BiSave size={18} />
                </div>
              }
              className="btn-submit ml-2"
              onClick={() => {
                markSubmit();
                onFinish();
              }}
              label="保存"
              style={{
                fontSize: size,
                height: `${height}px`,
                width: "fit-content",
                minWidth: "fit-content",
                gap: 5,
                ...getButtonStyle(),
              }}
              displaySize={displaySize}
            />
          </div>
        )}
        <Tabs
          defaultActiveKey={DEFAULT_TAB}
          activeKey={activeKey}
          onChange={onChange}
          style={{ marginRight: -36, marginLeft: -34 }}
          className="tab-custom"
        >
          <Tabs.TabPane tab="Bizシート設定" key={listKey[2]}>
            <div style={{ padding: "0 32px" }}>
              <AppFormInformation
                form={formik}
                getFieldClassName={getFieldClassName}
                registerFormWatcher={registerFormWatcher}
              />
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Bizシートレイアウト" key={listKey[0]}>
            <div style={{ padding: "0 32px" }}>
              <AppFormLayout />
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="フォームレイアウト" key={listKey[1]}>
            <div style={{ padding: "0 32px" }}>
              <AppFormLayout />
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="権限設定" key={listKey[3]}>
            <TabPermissionCreate />
          </Tabs.TabPane>
        </Tabs>
      </StylesAppFormCreate>
    </>
  );
};

export const AppFormCreate = () => (
  <AppFormContextProvider>
    <AppFormCreateLayout />
  </AppFormContextProvider>
);

const AppForm: FC<ReceivedProps> = (props) => (
  // <AppFormContextProvider>
  <AppFormLayout {...props} {...useFormAction()} />
  // </AppFormContextProvider>
);

export default AppForm;
